// React
import React from 'react';

// FutureLab
import { ChatListDataInterface, ChatParticipantsInterface } from '../ChatListItemsMobile';
import { ChatRoomItemsInterface } from '../ChatRoom';
import { useDeleteMessageMutation } from '../../../store/api/chatApi';
import { UserDetailsListItem } from '../../../model';
import { store } from '../../../ReduxRoot';
import { ModalConfirmation } from '../../popup/modalConfirmation';
import { ChatAttachmentPreview } from '../ChatAttachmentPreview';
import { RichTextMessage } from './RichTextMessage';

import { MoreVertical, Trash2 } from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

// 3rd Party
import _ from 'lodash';

interface ChatRoomMessageProps {
	loggedInUserParticipantId: ChatParticipantsInterface | undefined;
	chatItem: ChatRoomItemsInterface;
	setChatRoomItems: any;
	chatRoomItems: any;
	chatInFocus: ChatListDataInterface;
}

const renderChatMessage = (chatItem: ChatRoomItemsInterface) => {
	return (
		<>
			{_.isEmpty(chatItem.attachments) || chatItem.attachments?.[0] === null ? (
				<RichTextMessage message={chatItem.message} />
			) : (
				<div className="min-w-0">
					<div className="pb-1.5"><RichTextMessage message={chatItem.message} /></div>
					<ChatAttachmentPreview attachment={chatItem.attachments?.[0]} />
				</div>
			)}
		</>
	);
};

export function ChatRoomMessage(chatRoomMessageProps: ChatRoomMessageProps) {
	const { loggedInUserParticipantId, chatItem, setChatRoomItems, chatRoomItems, chatInFocus } = chatRoomMessageProps;
	const [deleteMessage] = useDeleteMessageMutation();
	const [displayModalConfirmation, setDisplayModalConfirmation] = React.useState<boolean>(false);
	const [optionStore, setOptionStore] = React.useState({});
	const chatSenderUserId = chatInFocus.participants.find((participant) => participant._id === chatItem.participantId);

	const chatSenderName: UserDetailsListItem | undefined = store
		.getState()
		.userListProfilesDetails.find(
			(participant: UserDetailsListItem) => participant?.id === chatSenderUserId?.userId
		);

	const chatSenderFullName = `${chatSenderName?.firstname} ${chatSenderName?.lastname ?? ''}`;

	const chatMessagesOption = [{ option: 'Delete Message', action: 'deleteMessage' }];
	const canDeleteMessage =
		loggedInUserParticipantId?._id === chatItem?.participantId ||
		loggedInUserParticipantId?._id === chatItem?.participant;
	const checkMessagesOption = canDeleteMessage ? chatMessagesOption : [];

	const deleteMessageCallBack = (chatItem: ChatRoomItemsInterface) => {
		deleteMessage({
			chatId: chatItem.chatId.toLocaleString(),
			messageId: chatItem.id.toLocaleString(),
		})
			.unwrap()
			.then((response) => {
			if (response.status) {
				const filterNonDeletedMessage = chatRoomItems.filter(
					(currentChatItem: { id: number | string; chatId: number | string }) =>
						`${currentChatItem.chatId}` !== `${chatItem.chatId}` || `${currentChatItem.id}` !== `${chatItem.id}`
				);
				setChatRoomItems(filterNonDeletedMessage);
				setDisplayModalConfirmation(false);
			}
		});
	};

	const renderMessageOption = () => {
		return checkMessagesOption.map((chatMessageOption) => (
			<DropdownMenuItem
				key={chatMessageOption?.option}
				className={chatMessageOption.option === 'Delete Message' ? 'min-h-10 text-destructive focus:text-destructive' : 'min-h-10'}
				onClick={() => {
					setOptionStore(chatMessageOption);
					setDisplayModalConfirmation(true);
				}}
			>
					{chatMessageOption.option === 'Delete Message' ? <Trash2 className="size-4" /> : null}
					{chatMessageOption.option}
			</DropdownMenuItem>
		));
	};

	return (
		<div className="min-w-0">
			{loggedInUserParticipantId?._id === chatItem?.participantId ||
			loggedInUserParticipantId?._id === chatItem?.participant ? (
				<div className="relative flex min-w-0 justify-end">
					<div className="mr-11 w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md border border-solid bg-blue-50 px-3.5 py-2.5 text-sm text-gray-900 shadow-sm md:max-w-[65%]">
						{renderChatMessage(chatItem)}
					</div>
					{canDeleteMessage && (
						<DropdownMenu>
							<DropdownMenuTrigger aria-label="Message options" className="absolute right-0 top-1/2 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
								<MoreVertical className="size-5" />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-40 text-center">
								{renderMessageOption()}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
					{displayModalConfirmation && (
						<ModalConfirmation
							displayModalConfirmation={displayModalConfirmation}
							setDisplayModalConfirmation={setDisplayModalConfirmation}
							optionStore={optionStore}
							callBackProceedMethodProp={() => deleteMessageCallBack(chatItem)}
						/>
					)}
				</div>
			) : (
				<div className="relative min-w-0">
					<div className="w-fit max-w-[85%] truncate px-1 pb-1 text-xs font-semibold text-gray-900 md:max-w-[65%]">
						{chatInFocus.isGroupChat ? chatSenderFullName : ''}
					</div>
					<div className="mr-11 w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm md:max-w-[65%]">
						{renderChatMessage(chatItem)}
					</div>
					{canDeleteMessage && (
						<DropdownMenu>
							<DropdownMenuTrigger aria-label="Message options" className="absolute right-0 top-1/2 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
								<MoreVertical className="size-5" />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-40 text-center">
								{renderMessageOption()}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
					{displayModalConfirmation && (
						<ModalConfirmation
							displayModalConfirmation={displayModalConfirmation}
							setDisplayModalConfirmation={setDisplayModalConfirmation}
							optionStore={optionStore}
							callBackProceedMethodProp={() => deleteMessageCallBack(chatItem)}
						/>
					)}
				</div>
			)}
		</div>
	);
}
